import React, { useEffect, useState, useCallback } from "react";
import {
  MapPin, Phone, Package, CheckCircle, Clock, ArrowsClockwise, X,
} from "@phosphor-icons/react";
import { staffApi } from "../../lib/staffApi";
import ThermalPrintButton from "../../components/print/ThermalPrintButton";
import { buildDeliveryReceipt } from "../../lib/receiptBuilder";

const COLOR_SWATCH = {
  yellow: "#F5C518", blue: "#1E5FA0", red: "#C0392B",
  white: "#F4F4F4", black: "#1A1A1A", grey: "#7A7A7A",
};

function todayIsoLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function StaffDeliveries() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinModal, setPinModal] = useState(null); // challan object

  const load = useCallback(async () => {
    try {
      const r = await staffApi.get("/staff-app/deliveries");
      setRows(r.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Iter 132 — deep-link ?open=<challan_id> (from StaffTaskDetail banner).
  // Once the rows are loaded, auto-open the PIN modal for that challan
  // and highlight it. The URL is scrubbed after use so a refresh doesn't
  // re-trigger the modal.
  useEffect(() => {
    if (!rows.length || typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const openId = sp.get("open");
    if (!openId) return;
    const target = rows.find((d) => d.id === openId);
    if (target) setPinModal(target);
    // Clean up the URL
    const url = new URL(window.location.href);
    url.searchParams.delete("open");
    window.history.replaceState(null, "", url.pathname);
  }, [rows]);

  const startDelivery = (d) => setPinModal(d);

  const today = todayIsoLocal();
  const todayRows = rows.filter((d) => (d.delivery_date || "") === today);
  const backlogRows = rows.filter(
    (d) => (d.delivery_date || "") && d.delivery_date < today,
  );
  const upcomingRows = rows.filter(
    (d) => !d.delivery_date || d.delivery_date > today,
  );

  return (
    <div className="space-y-5" data-testid="staff-deliveries-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">
            Bag deliveries · {rows.length} pending
          </div>
          <h1 className="font-heading text-xl text-[#1A1A1A]">My deliveries</h1>
        </div>
        <button onClick={load} data-testid="staff-deliveries-reload"
                className="h-9 px-3 border border-[#E5E2D9] rounded text-[11px] uppercase tracking-[0.16em] text-[#5C5A55] inline-flex items-center gap-1.5">
          <ArrowsClockwise size={12} />Refresh
        </button>
      </div>

      {loading && <div className="text-xs text-[#8A8780]">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="bg-white border border-[#E5E2D9] rounded-lg p-8 text-center"
              data-testid="staff-deliveries-empty">
          <Package size={30} className="inline-block mb-3 opacity-40" />
          <div className="text-sm text-[#5C5A55]">No pending deliveries</div>
          <div className="text-xs text-[#8A8780] mt-1">
            Assigned bag delivery challans will show up here.
          </div>
        </div>
      )}

      {backlogRows.length > 0 && (
        <Section
          testId="staff-deliveries-backlog"
          title={`Backlog · ${backlogRows.length} from earlier days`}
          tone="warn">
          {backlogRows.map((d) => (
            <DeliveryCard key={d.id} d={d} onStart={startDelivery} tone="warn" />
          ))}
        </Section>
      )}

      {todayRows.length > 0 && (
        <Section
          testId="staff-deliveries-today"
          title={`Today · ${todayRows.length}`}
          tone="primary">
          {todayRows.map((d) => (
            <DeliveryCard key={d.id} d={d} onStart={startDelivery} tone="primary" />
          ))}
        </Section>
      )}

      {upcomingRows.length > 0 && (
        <Section
          testId="staff-deliveries-upcoming"
          title={`Upcoming · ${upcomingRows.length}`}
          tone="muted">
          {upcomingRows.map((d) => (
            <DeliveryCard key={d.id} d={d} onStart={startDelivery} tone="muted" />
          ))}
        </Section>
      )}

      {pinModal && (
        <PinCompleteModal
          d={pinModal}
          onClose={() => setPinModal(null)}
          onSuccess={() => { setPinModal(null); load(); }}
        />
      )}
    </div>
  );
}

const Section = ({ title, testId, tone, children }) => {
  const toneCls = tone === "warn"
    ? "text-[#7A2E18]"
    : tone === "muted"
      ? "text-[#5C5A55]"
      : "text-[#0D47A1]";
  const Icon = tone === "warn" ? Clock : Package;
  return (
    <section data-testid={testId}>
      <div className={`text-[11px] uppercase tracking-[0.2em] font-semibold mb-2 inline-flex items-center gap-1.5 ${toneCls}`}>
        <Icon size={12} weight="fill" />{title}
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
};

const DeliveryCard = ({ d, onStart, tone }) => {
  const cardCls = tone === "warn"
    ? "bg-[#F9EDE7] border border-[#F4C7B9]"
    : tone === "muted"
      ? "bg-white border border-[#E5E2D9] opacity-90"
      : "bg-[#DDE9F6]/40 border border-[#BBDEFB]";
  const swatch = COLOR_SWATCH[d.colour_code] || "#7A7A7A";
  const navUrl = d.customer_latitude && d.customer_longitude
    ? `https://www.google.com/maps/dir/?api=1&destination=${d.customer_latitude},${d.customer_longitude}`
    : null;
  const statusLabel = d.status === "out_for_delivery"
    ? "OUT FOR DELIVERY"
    : (d.status || "assigned").toUpperCase();
  return (
    <div data-testid={`staff-delivery-${d.id}`}
          className={`rounded-lg p-4 ${cardCls}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] text-[#0D47A1] font-mono">{d.challan_no}</div>
            {d.delivery_date && (
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C5A55]">
                {d.delivery_date}
              </div>
            )}
            <div className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded font-semibold bg-white/70 text-[#0D47A1]">
              {statusLabel}
            </div>
          </div>
          <div className="font-semibold text-sm text-[#1A1A1A] truncate mt-0.5">
            {d.customer_name || "—"}
          </div>
          <div className="text-[11px] text-[#5C5A55] mt-0.5 flex items-center gap-1.5">
            {d.colour_code && (
              <span className="w-2.5 h-2.5 rounded-full border border-black/10"
                    style={{ background: swatch }}/>
            )}
            {d.waste_category}
            {d.colour_code ? ` · ${d.colour_code.toUpperCase()}` : ""}
            {" · "}{d.count} bag{d.count > 1 ? "s" : ""}
            {" · "}₹{Number(d.total_cost || 0).toFixed(2)}
          </div>
          {d.customer_phone && (
            <a href={`tel:${d.customer_phone}`}
                className="text-[11px] text-[#2D5A27] mt-1 font-mono inline-flex items-center gap-1">
              <Phone size={10} weight="fill" />{d.customer_phone}
            </a>
          )}
          {d.customer_address && (
            <div className="text-[11px] text-[#5C5A55] mt-0.5 flex items-start gap-1">
              <MapPin size={10} className="mt-0.5 shrink-0" />
              <span className="min-w-0">{d.customer_address}</span>
            </div>
          )}
          {d.first_code && (
            <div className="text-[10px] text-[#0D47A1] font-mono mt-1.5 bg-white/60 rounded px-1.5 py-0.5 inline-block">
              {d.first_code} → {d.last_code}
            </div>
          )}
          {/* Iter 74 — customer's declared primary waste (D-BMW). */}
          {(d.primary_waste_names || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1" data-testid={`staff-delivery-primary-waste-${d.id}`}>
              {d.primary_waste_names.slice(0, 4).map((n) => (
                <span key={n}
                      className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F9F0D6] border border-[#F5C518]/50 text-[#7A5A11] font-semibold">
                  {n}
                </span>
              ))}
              {d.primary_waste_names.length > 4 && (
                <span className="text-[9px] text-[#8A8780]">+{d.primary_waste_names.length - 4}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          {navUrl && (
            <a href={navUrl} target="_blank" rel="noreferrer"
                data-testid={`staff-delivery-nav-${d.id}`}
                className="h-9 px-3 border border-[#0D47A1] text-[#0D47A1] text-xs rounded font-medium inline-flex items-center gap-1">
              <MapPin size={12} />Navigate
            </a>
          )}
          <button onClick={() => onStart(d)}
                  data-testid={`staff-delivery-start-${d.id}`}
                  className="h-9 px-3 bg-[#0D47A1] hover:bg-[#0B3B85] text-white text-xs rounded font-medium inline-flex items-center gap-1">
            <CheckCircle size={12} weight="fill" />Start delivery
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Two-step PIN gate + signer capture. Field-staff enters the 6-digit
 * delivery PIN the customer received at request time, then optionally
 * captures a signer phone (e.g. security/caretaker), then hits
 * Complete → server validates PIN and flips challan to delivered.
 */
const PinCompleteModal = ({ d, onClose, onSuccess }) => {
  const [pin, setPin] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signerPhone, setSignerPhone] = useState("");
  const [signerRelation, setSignerRelation] = useState("customer");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Iter 81 — after PIN completes, we flip to a small "Delivered"
  // screen with a Bluetooth print button so staff can hand a slip
  // to the customer/receptionist right away.
  const [delivered, setDelivered] = useState(null);

  const complete = async () => {
    if (!/^\d{6}$/.test(pin)) {
      setErr("Enter the 6-digit PIN shared with the customer.");
      return;
    }
    if (!signerName.trim()) {
      setErr("Signer name is required.");
      return;
    }
    setBusy(true); setErr(null);
    let gps = null;
    try {
      gps = await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          (p) => resolve({
            latitude: p.coords.latitude, longitude: p.coords.longitude,
            accuracy: Math.round(p.coords.accuracy),
          }),
          () => resolve(null),
          { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
        );
      });
    } catch { /* silent */ }
    try {
      await staffApi.post(`/staff-app/deliveries/${d.id}/complete`, {
        delivery_pin: pin,
        signed_by_name: signerName.trim(),
        signed_by_phone: signerPhone.trim() || null,
        signed_by_relation: signerRelation,
        ...(gps || {}),
      });
      // Snapshot enough context to build the thermal slip.
      setDelivered({
        request_no: d.request_no || d.challan_no,
        challan_no: d.challan_no,
        delivery_pin: pin,
        status: "delivered",
        customer: {
          name: d.customer_name,
          phone: d.customer_phone,
          address: d.customer_address,
        },
        items: [{
          category: `${d.waste_category || "Bag"}${d.colour_code ? ` · ${d.colour_code}` : ""}`,
          qty: d.count,
        }],
        signer_name: signerName.trim(),
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not complete delivery.");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3">
      <div className="bg-white rounded-t-lg sm:rounded-lg w-full max-w-md"
            data-testid="staff-delivery-pin-modal">
        {delivered ? (
          <>
            <div className="flex items-center justify-between p-4 border-b border-[#E5E2D9]">
              <div>
                <div className="font-heading text-base text-[#2D5A27] inline-flex items-center gap-1.5">
                  <CheckCircle size={16} weight="fill" />Delivered
                </div>
                <div className="text-[11px] text-[#5C5A55]">
                  {d.challan_no} · {d.customer_name}
                </div>
              </div>
              <button onClick={onSuccess}
                      data-testid="staff-delivery-done-close"
                      className="p-1.5 hover:bg-[#F7F6F2] rounded">
                <X size={16} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-xs text-[#5C5A55]">
                Delivery marked complete. Print a slip if the customer wants a hard copy.
              </div>
              <ThermalPrintButton
                label="Print delivery slip"
                variant="primary"
                dataTestId="staff-delivery-print-slip"
                className="w-full h-11"
                build={() => buildDeliveryReceipt(delivered)}
              />
              <button onClick={onSuccess}
                      data-testid="staff-delivery-done-btn"
                      className="w-full h-11 border border-[#E5E2D9] rounded text-sm text-[#5C5A55]">
                Done
              </button>
            </div>
          </>
        ) : (
        <>
        <div className="flex items-center justify-between p-4 border-b border-[#E5E2D9]">
          <div>
            <div className="font-heading text-base">Complete delivery</div>
            <div className="text-[11px] text-[#5C5A55]">
              {d.challan_no} · {d.customer_name}
            </div>
          </div>
          <button onClick={onClose} data-testid="staff-delivery-pin-close"
                  className="p-1.5 hover:bg-[#F7F6F2] rounded">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
              6-digit delivery PIN
            </label>
            <input type="tel"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={6}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    data-testid="staff-delivery-pin-input"
                    className="mt-1 w-full h-12 px-3 border border-[#E5E2D9] rounded text-lg font-mono tracking-[0.4em] text-center focus:outline-none focus:border-[#0D47A1]"
                    placeholder="••••••" />
            <div className="text-[10px] text-[#5C5A55] mt-1">
              Ask the customer for the PIN. If they&apos;ve lost it, call office to regenerate.
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
              Signer name <span className="text-[#C25D38]">*</span>
            </label>
            <input value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    data-testid="staff-delivery-signer-name"
                    className="mt-1 w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm"
                    placeholder="Full name of the person receiving the bags" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
              Relation
            </label>
            <select value={signerRelation}
                    onChange={(e) => setSignerRelation(e.target.value)}
                    data-testid="staff-delivery-signer-relation"
                    className="mt-1 w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm">
              <option value="customer">Customer</option>
              <option value="family">Family member</option>
              <option value="security">Security guard</option>
              <option value="caretaker">Caretaker</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
              Signer phone <span className="text-[#8A8780]">(optional — for handover to security/caretaker)</span>
            </label>
            <input type="tel"
                    inputMode="tel"
                    value={signerPhone}
                    onChange={(e) => setSignerPhone(e.target.value)}
                    data-testid="staff-delivery-signer-phone"
                    className="mt-1 w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm font-mono"
                    placeholder="10-digit mobile" />
          </div>

          {err && (
            <div className="text-[12px] text-[#7A2E18] bg-[#F9EDE7] border border-[#F4C7B9] rounded p-2"
                  data-testid="staff-delivery-pin-error">{err}</div>
          )}
        </div>
        <div className="p-4 border-t border-[#E5E2D9] flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="h-10 px-4 border border-[#E5E2D9] rounded text-sm">
            Cancel
          </button>
          <button onClick={complete} disabled={busy}
                  data-testid="staff-delivery-complete-btn"
                  className="h-10 px-5 bg-[#0D47A1] hover:bg-[#0B3B85] disabled:opacity-60 text-white rounded text-sm font-medium inline-flex items-center gap-1">
            <CheckCircle size={14} weight="fill" />
            {busy ? "Verifying…" : "Complete delivery"}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
};
